/**
 * Mirrors the backend's order DTOs (menu_core_be, src/domain/orders/dto).
 *
 * Note what is NOT here: quantity. CreateOrderDishDto has no such field — the
 * server stores one row per ordered unit, so a cart line of quantity 3 becomes
 * three identical entries in `dishes`. See `buildOrderPayload.ts`.
 */

export interface CreateOrderTopping {
  id: string;
  quantity: number;
}

export interface CreateOrderDish {
  dishId: string;
  sizeId?: string;
  extraNote?: string;
  toppings: CreateOrderTopping[];
}

export interface CreateOrderGuest {
  guestNumber: number;
  dishes: CreateOrderDish[];
}

export interface CreateOrderPayload {
  tableId: string;
  guests: CreateOrderGuest[];
}

/** What the guest is paying, in grosze. */
export interface OrderPaymentDetails {
  method: "custom";
  /**
   * The dishes alone. The backend charges `amount + tip + its fee on the tip`,
   * so anything added here would be charged twice.
   */
  amountGrosze: number;
  /** The tip the recipient receives. The venue's fee is added by the server. */
  tipGrosze: number;
  /** Display only, the backend has no field for it yet. */
  paymentMethodName: string | null;
}

export interface PlaceOrderResult {
  orderId: string;
  /**
   * The Viva order the guest still has to pay. Empty from the mock, and empty
   * is not "free": see the note in useSubmitOrder.
   */
  orderCode: string;
  paidAmountGrosze: number;
}

/**
 * The order reached the kitchen but the payment did not go through.
 *
 * Worth its own type because the two halves are not equally recoverable: the
 * dishes are on the table's bill and will be cooked, so "try again" must mean
 * "pay the bill", never "order again". The mock could not produce this state,
 * which is why nothing distinguished it before.
 */
export class OrderPlacedPaymentFailedError extends Error {
  readonly orderId: string;

  /** What the payment call threw, kept for logging. */
  readonly reason: unknown;

  constructor(orderId: string, reason?: unknown) {
    super("The order was placed but the payment failed");
    this.name = "OrderPlacedPaymentFailedError";
    this.orderId = orderId;
    this.reason = reason;
  }
}
